import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { CustomerSiigoType, CustomerType } from '../types';

// Define a type for the slice state
interface CustomerState {
  customers: CustomerType[];
  customer: CustomerType;
  currentPage: number;
  rowsPerPage: number;
}

// Define the initial state using that type
const initialState: CustomerState = {
  customers: [],
  customer: {
    id: 0,
    siigo_id: '',
    alegra_id: '',
    customer_name: '',
    customer_city: {
      id: 0,
      city_name: '',
      state_name: '',
      country_name: '',
    },
  },
  currentPage: 1,
  rowsPerPage: 10,
};

export const customerSlice = createSlice({
  name: 'customer',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    // Use the PayloadAction type to declare the contents of `action.payload`
    setCustomers: (state, action: PayloadAction<CustomerType[]>) => {
      state.customers = action.payload;
    },
    setCustomer: (state, action: PayloadAction<CustomerType>) => {
      state.customer = action.payload;
    },
    updateCustomer: (state, action: PayloadAction<CustomerSiigoType>) => {
      const index = state.customers.findIndex(
        (c) =>
          c.siigo_id === action.payload.id ||
          c.alegra_id === action.payload.id ||
          c.id.toString() === action.payload.id,
      );

      if (index !== -1) {
        const city = action.payload.address?.city;
        if (city && action.payload.city_id) {
          state.customers[index] = {
            ...state.customers[index],
            customer_city: {
              city_name: city.city_name,
              state_name: city.state_name,
              country_name: city.country_name,
              id: action.payload.city_id,
            },
          };
        }
      }
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
    },
  },
});

export const { setCustomers, setCustomer, updateCustomer, setCurrentPage, setRowsPerPage } = customerSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectCustomer = (state: RootState) => state.customer;
export const selectCustomers = (state: RootState) => state.customer.customers;
export const initialStateCustomer = initialState;
export default customerSlice.reducer;
